import React, { useEffect, useState, useRef } from 'react';
import { listOrders, updateOrderStatus } from '../../api';

const BUTTON_COLORS = {
  preparacion: 'bg-blue-600 hover:bg-blue-700',
  porSalir:    'bg-orange-600 hover:bg-orange-700',
  listo:       'bg-green-600 hover:bg-green-700'
};

const STATUS_COLORS = {
  recibido:    'border-yellow-500 bg-yellow-100',
  preparacion: 'border-blue-500 bg-blue-100',
  porSalir:    'border-orange-500 bg-orange-100',
  listo:       'border-green-500 bg-green-100'
};

const STATUS_LABELS = {
  recibido:    'Recibido',
  preparacion: 'En Preparación',
  porSalir:    'Listo para Recoger',
  listo:       'Entregado'
};

const NEXT_STATUS = {
  recibido:    'preparacion',
  preparacion: 'porSalir',
  porSalir:    'listo'
};

const ComandasPage = ({ employees }) => {

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  const prevOrderIds  = useRef([]);
  const prevOrdersRef = useRef([]);
  const audioRef      = useRef(null);

  useEffect(() => {
    const audio = new Audio(window.location.origin + '/sounds/alert.mp3');
    audio.preload = 'auto';
    audioRef.current = audio;

    const unlockAudio = async () => {
      try {
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        console.log("🔊 Audio desbloqueado");
      } catch (e) {
        console.warn("❌ No se pudo desbloquear audio:", e);
      }
      document.removeEventListener('click', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
  }, []);

  const playSynthSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();

      const beep = (freq, start, duration) => {
        const osc      = ctx.createOscillator();
        const gainNode = ctx.createGain();
        osc.connect(gainNode);
        gainNode.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type            = 'sine';
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime + start);
        gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + duration);
        osc.start(ctx.currentTime + start);
        osc.stop(ctx.currentTime + start + duration);
      };

      beep(880,  0,   0.15);
      beep(1100, 0.2, 0.15);
      beep(1320, 0.4, 0.2);

    } catch (err) {
      console.warn("❌ Web Audio API no disponible:", err);
    }
  };

  const playSound = () => {
    if (navigator.vibrate) {
      navigator.vibrate([300, 100, 300]);
    }

    if (!audioRef.current) {
      playSynthSound();
      return;
    }

    try {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      const p = audioRef.current.play();
      if (p !== undefined) {
        p.then(() => console.log("✅ SONIDO OK"))
         .catch(err => {
           console.warn("❌ Audio mp3 bloqueado, usando sintetizador:", err);
           playSynthSound();
         });
      }
    } catch (err) {
      console.error("❌ Error audio:", err);
      playSynthSound();
    }
  };

  const fetchOrders = async () => {
    try {
      const res = await listOrders();

      if (res.success) {
        const newOrders  = res.data || [];
        const currentIds = newOrders.map(o => o.order_id);

        // FIX: orden nueva por ID
        const hasNewOrder = prevOrderIds.current.length > 0 &&
          currentIds.some(id => !prevOrderIds.current.includes(id));

        // FIX: orden actualizada solo si tiene items con is_new=1 Y updated_at cambió
        // Esto evita que suene al cerrar/cobrar una orden
        const hasUpdatedOrder = prevOrderIds.current.length > 0 &&
          newOrders.some(order => {
            const prev = prevOrdersRef.current.find(o => o.order_id === order.order_id);
            if (!prev) return false;
            const hasNewItems = order.items?.some(i => i.is_new === 1);
            const timeChanged = order.updated_at && prev.updated_at &&
              order.updated_at !== prev.updated_at;
            // FIX: solo sonar si hay items nuevos reales, no por cierre de orden
            return hasNewItems && timeChanged;
          });

        if (hasNewOrder || hasUpdatedOrder) {
          console.log(hasNewOrder ? "🚨 NUEVA ORDEN" : "🔄 ORDEN ACTUALIZADA");
          playSound();
        }

        prevOrderIds.current  = currentIds;
        prevOrdersRef.current = newOrders;
        setOrders(newOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      const res = await updateOrderStatus(orderId, newStatus);
      if (res.success) {
        setOrders(prev =>
          prev.map(o =>
            o.order_id === orderId ? { ...o, comanda_status: newStatus } : o
          )
        );
        fetchOrders();
      } else {
        alert('Error al actualizar status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const activeOrders = orders
    .filter(o => (o.comanda_status || 'recibido') !== 'listo')
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at.replace(' ', 'T')) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at.replace(' ', 'T')) : new Date(0);
      return dateA - dateB;
    });

  if (loading) {
    return <div className="text-center mt-10 text-gray-500">Cargando comandas...</div>;
  }

  return (
    <div className="mt-6 space-y-4">

      <h3 className="text-xl font-semibold text-[#3F1907]">Comandas</h3>

      {activeOrders.length === 0 && (
        <div className="text-center text-gray-500 mt-10">No hay comandas activas</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeOrders.map(order => {

          const status   = order.comanda_status || 'recibido';
          const employee = employees?.find(
            e => e.id === order.employee_id || e.id === order.user_id
          ) || null;

          const hasNewItems   = order.items?.some(i => i.is_new === 1);
          const hasOldItems   = order.items?.some(i => i.is_new === 0);
          const isReactivated = hasNewItems && hasOldItems;

          return (
            <div
              key={order.order_id}
              className={`border-l-4 rounded-lg shadow p-4 transition-all duration-300 ${STATUS_COLORS[status] || STATUS_COLORS.recibido}`}
            >
              <div className="flex justify-between items-center mb-2">
                <div>
                  <span className="font-bold text-[#3F1907]">
                    {order.order_name
                      ? order.order_name
                      : `Comanda #${order.order_number}`}
                  </span>
                  {order.order_name && (
                    <p className="text-xs text-gray-400">#{order.order_number}</p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm font-semibold text-[#58735A]">
                    {STATUS_LABELS[status] || 'Recibido'}
                  </span>
                  {/* FIX: badge inline para Kodular */}
                  {isReactivated && (
                    <span style={{
                      fontSize: '11px',
                      background: '#facc15',
                      color: '#713f12',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontWeight: '700'
                    }}>
                      ACTUALIZADA
                    </span>
                  )}
                </div>
              </div>

              <div className="text-sm text-gray-600 mb-2">
                {order.created_at
                  ? new Date(order.created_at.replace(' ', 'T')).toLocaleString('es-MX', {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit'
                    })
                  : 'Sin fecha'}
              </div>

              {employee && (
                <div className="text-sm text-[#3F1907] mb-2">
                  Atendió: <strong>{employee.name}</strong>
                </div>
              )}

              {order.items && (
                <ul className="text-sm mb-4 space-y-2">
                  {order.items.map((item, idx) => {
                    const isNew = item.is_new === 1;
                    return (
                      // FIX: estilos inline para compatibilidad con Kodular
                      <li
                        key={idx}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          paddingBottom: '8px',
                          borderRadius: '4px',
                          padding: '4px',
                          backgroundColor: isNew && isReactivated ? '#fefce8' : 'transparent',
                          opacity: isNew ? 1 : 0.45
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          fontWeight: '600',
                          // FIX: textDecoration inline para Kodular
                          textDecoration: isNew ? 'none' : 'line-through',
                          color: isNew ? '#3F1907' : '#9ca3af'
                        }}>
                          <span>
                            {isNew && isReactivated && (
                              <span style={{
                                marginRight: '4px',
                                fontSize: '11px',
                                background: '#facc15',
                                color: '#713f12',
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontWeight: '700'
                              }}>
                                NUEVO
                              </span>
                            )}
                            {item.product_name}
                          </span>
                          <span>x{item.quantity}</span>
                        </div>

                        {item.variants && item.variants.length > 0 && (
                          <p style={{
                            fontSize: '12px',
                            marginLeft: '4px',
                            marginTop: '2px',
                            textDecoration: isNew ? 'none' : 'line-through',
                            color: isNew ? '#4b5563' : '#d1d5db'
                          }}>
                            {item.variants.map(v => v.variant_name).join(', ')}
                          </p>
                        )}

                        {item.note && (
                          <div style={{
                            fontSize: '12px',
                            fontStyle: 'italic',
                            marginLeft: '4px',
                            textDecoration: isNew ? 'none' : 'line-through',
                            color: isNew ? '#6b7280' : '#d1d5db'
                          }}>
                            Nota: {item.note}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}

              {NEXT_STATUS[status] && (
                <button
                  onClick={() => handleStatusChange(order.order_id, NEXT_STATUS[status])}
                  className={`w-full text-white py-2 rounded-md transition ${BUTTON_COLORS[NEXT_STATUS[status]]}`}
                >
                  Marcar como {STATUS_LABELS[NEXT_STATUS[status]]}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComandasPage;